import {
  Component,
  DOCUMENT,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';
import { SideMenus } from '../../../shared/constants/menus';
import { environment } from '../../../../environments/environment';
import { NgClass } from '@angular/common';
import { ChatbotService } from '../../../shared/services/chatbot.service';
import { SharedService } from '../../../shared/services/shared.service';
import { Subscription } from 'rxjs';
import { FieldValue } from '../../../shared/constants/services.enum';
import { ButtonComponent } from '../../../shared/components/button/button.component';
interface SideLink {
  icon: string;
  key: string;
  route: string;
  badge?: number;
  active: boolean;
}
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslateModule, NgClass, ButtonComponent],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
})
export class SidebarComponent implements OnDestroy {
  @Input() open = false;
  @Output() close = new EventEmitter<void>();
  // This method is used to handle esc event


  @HostListener('document:keydown.escape')
  onEsc() {
    if (this.open) this.close.emit();
  }
  menus: SideLink[] = SideMenus;
  // MVP1 flags (default to true when undefined)
  flags = {
    chatEnabled: true,
    bgcEnabled: environment.mVP1Settings?.bgcEnabled ?? true,
    accountManagementEnabled:
      environment.mVP1Settings?.accountManagementEnabled ?? true,
    ataEnabled: true,
    mediationEnabled: environment.mVP1Settings?.mediationEnabled ?? true,
    documentsEnabled: environment.mVP1Settings?.documentsEnabled ?? true,
    venueEnabled: environment.mVP1Settings?.venueEnabled ?? true,
  };
  lang: any = 'en';
  collapsed: boolean = false;
  isRtl: boolean = false;

  primaryAccount: any = null;
  private primaryAccountSubscription: Subscription | null = null;
  private dcUserTypeSubscription: Subscription | null = null;
  private readonly businessGroupKey = 'MENU_BUSINESS_GROUP';
  showBusinessGroupRedirectModal = false;
  redirectCountdownSeconds = 5;
  private redirectCountdownTimer: ReturnType<typeof setInterval> | null = null;
  constructor(
    private languageService: LanguageService,
    private sharedService: SharedService,
    private chatbot: ChatbotService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.lang = this.languageService.getCurrentLang();
    this.isRtl = this.document.documentElement.dir === 'rtl';
    this.primaryAccount = this.sharedService.getPrimaryAccount();
    this.primaryAccountSubscription = this.sharedService.primaryAccount$.subscribe(
      (account) => {
        this.primaryAccount = account;
        this.refreshMenus();
      }
    );
    this.dcUserTypeSubscription = this.sharedService.dcUserType$.subscribe(() => {
      this.refreshMenus();
    });

    // filter menus based on flags
    this.refreshMenus();
  }

  get isDCHIStartDateAvailable() {
    return this.primaryAccount?.['DC HI Start Date'] ? true : false;
  }

  private refreshMenus(): void {
    const loginUserDetail = this.sharedService.getUserProfile();
    //user type for selected membership
    const isAdmin = this.sharedService.getDcUserType() === 'Administrator';

    this.menus = SideMenus.filter((m) => {
      if (m.key === 'MENU_ACCOUNT') {
        return this.flags.accountManagementEnabled;
      }
      if (m.key === 'MENU_BUSINESS_GROUP') {
        return this.flags.bgcEnabled;
      }
      if (m.key === 'MENU_ATA') {
        return this.flags.ataEnabled;
      }
      if (m.key === 'MENU_MEDIATION') {
        return this.flags.mediationEnabled;
      }

      if (m.key === 'MENU_DOCUMENTS' && !isAdmin) {
        return false;
      }
      if (m.key === 'MENU_VENUE') {
        return this.flags.venueEnabled;
      }
      if (m.key === 'MENU_MRM_AWARDS') { //&& !this.isDCHIStartDateAvailable //removed as per puneet metnion show for all 7 Apirl 2026
        return true;
      }
      if (m.key === 'MENU_CHAMBER_ENTREPRENEURSHIP'){
        return true;
      }
      // if (m.key === 'MENU_CHAMBER_ENTREPRENEURSHIP'
      //   && (loginUserDetail['DC Nationality'] !== FieldValue.UAE && loginUserDetail['DC Nationality'] !== FieldValue.UAE_AR)) {
      //   return false;
      // }
      return m.active;
    });
  }

  ngOnDestroy(): void {
    this.primaryAccountSubscription?.unsubscribe();
    this.dcUserTypeSubscription?.unsubscribe();
    this.clearRedirectCountdownTimer();
  }
  // This method is used to toggle sidebar
  toggleSidebar() {
    const sidebar: any = document.querySelector('.sidebar');
    const icon: any = document.querySelector('span i');
    sidebar.classList.toggle('collapsed');
    this.document.body.classList.toggle('sidebar-collapsed', this.collapsed);

    icon.classList.toggle('fa-angle-left');
    icon.classList.toggle('fa-angle-right');
  }

  get isPlatinumTheme(): boolean {
    return this.document?.body?.classList.contains('platinum-theme');
  }

  getIconPath(icon: string): string {
    const fileName = icon?.split('/').pop() ?? icon;
    if (this.isPlatinumTheme) {
      return `/icons/platinum/${fileName}`;
    }
    return `/icons/${icon}`;
  }
  collapseSidebar(menu: string, event?: MouseEvent) {
    if (menu === this.businessGroupKey) {
      event?.preventDefault();
      event?.stopPropagation();
      this.showBusinessGroupRedirectModal = true;
      this.startRedirectCountdown();
      return;
    }
    if (menu == "MENU_STATEMENT_OF_ACCOUNT" && !this.collapsed) {
      this.collapsed = true;
      this.toggleSidebar();
    }
    if (this.open) {
      this.close.emit();
    }
  }

  closeBusinessGroupRedirectModal(): void {
    this.clearRedirectCountdownTimer();
    this.showBusinessGroupRedirectModal = false;
  }

  redirectBusinessGroup(): void {
    this.clearRedirectCountdownTimer();
    this.showBusinessGroupRedirectModal = false;
    window.open(environment.businessGroupPlatformUrl,'_self');
    if (this.open) {
      this.close.emit();
    }
  }

  private startRedirectCountdown(): void {
    this.clearRedirectCountdownTimer();
    this.redirectCountdownSeconds = 5;
    this.redirectCountdownTimer = setInterval(() => {
      this.redirectCountdownSeconds -= 1;
      if (this.redirectCountdownSeconds <= 0) {
        this.redirectBusinessGroup();
      }
    }, 1000);
  }

  private clearRedirectCountdownTimer(): void {
    if (this.redirectCountdownTimer) {
      clearInterval(this.redirectCountdownTimer);
      this.redirectCountdownTimer = null;
    }
  }
  // This method is for open the chatboat
  async onChatWithUs(event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    try {
      await this.chatbot.open();
    } catch (err) {
      console.error(err);
      // Optional: show toast/snackbar
      // this.toast.error('Chatbot is not available right now.');
    }
  }
}
